import { ErrorBoundary } from "@sentry/react"
import { CSSProperties, ReactNode } from "react"

import ErrorFallbackCard from "#blocks/ErrorFallbackCard"
import ErrorFallbackLine from "#blocks/ErrorFallbackLine"

type ErrorFallbackVariant = "card" | "line"

interface ComponentErrorBoundaryProps {
  children: ReactNode
  /** "card" for tiles/sections, "line" for inline text/rows. Default: "card" */
  variant?: ErrorFallbackVariant
  /** Context string for logging and display (e.g. "Class: Advanced Python") */
  context?: string
  /** Style to apply to fallback to preserve layout dimensions */
  fallbackStyle?: CSSProperties
  /** Optional custom reset handler */
  onReset?: () => void
}

/**
 * Granular error boundary for individual components.
 * Catches errors and displays an inline fallback instead of crashing the parent.
 *
 * Usage:
 * ```tsx
 * // Card variant (default) - for tiles, cards, sections
 * <ComponentErrorBoundary context="Class: Python 101" fallbackStyle={{ width: 200 }}>
 *   <StudentAttendanceListClass ... />
 * </ComponentErrorBoundary>
 *
 * // Line variant - for list rows, table cells, inline text
 * <ComponentErrorBoundary variant="line" context="Student name">
 *   <StudentName studentId={id} />
 * </ComponentErrorBoundary>
 * ```
 */
const ComponentErrorBoundary = ({
  children,
  variant = "card",
  context,
  fallbackStyle,
  onReset,
}: ComponentErrorBoundaryProps) => {
  const FallbackComponent =
    variant === "line" ? ErrorFallbackLine : ErrorFallbackCard

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        if (context) {
          scope.setTag("component_context", context)
        }
      }}
      fallback={({ error, resetError }) => (
        <FallbackComponent
          error={error as Error}
          context={context}
          style={fallbackStyle}
          onRetry={onReset ?? resetError}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  )
}

export default ComponentErrorBoundary
